'use client';

import { CheckCircle2, XCircle, AlertTriangle, Mail, X } from 'lucide-react';
import { useEscapeKey } from '@/hooks/useEscapeKey';

interface BatchSummaryModalProps {
  open: boolean;
  created: number;
  skipped: number;
  failed: number;
  total: number;
  onClose: () => void;
}

export function BatchSummaryModal({
  open,
  created,
  skipped,
  failed,
  total,
  onClose,
}: BatchSummaryModalProps) {
  useEscapeKey(onClose, open);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-base-100/95" onClick={onClose} />
      <div className="relative bg-base-100 border border-base-300/30 rounded-2xl p-6 w-full max-w-sm mx-4 space-y-5 animate-fade-in shadow-2xl">
        <button onClick={onClose} className="absolute top-4 right-4 btn btn-ghost btn-xs btn-circle">
          <X size={14} />
        </button>

        <div className="text-center space-y-2">
          <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto">
            <Mail size={24} className="text-primary" />
          </div>
          <h2 className="text-lg font-bold">Batch Complete</h2>
          <p className="text-sm text-base-content/40">
            {total} email{total !== 1 ? 's' : ''} processed
          </p>
        </div>

        <div className="space-y-2">
          {created > 0 && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-success/5 border border-success/10">
              <CheckCircle2 size={16} className="text-success shrink-0" />
              <span className="text-sm font-medium flex-1">Drafts created</span>
              <span className="text-sm font-bold text-success">{created}</span>
            </div>
          )}
          {skipped > 0 && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-warning/5 border border-warning/10">
              <AlertTriangle size={16} className="text-warning shrink-0" />
              <span className="text-sm font-medium flex-1">Skipped</span>
              <span className="text-sm font-bold text-warning">{skipped}</span>
            </div>
          )}
          {failed > 0 && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-error/5 border border-error/10">
              <XCircle size={16} className="text-error shrink-0" />
              <span className="text-sm font-medium flex-1">Failed</span>
              <span className="text-sm font-bold text-error">{failed}</span>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <a
            href="https://mail.google.com/#drafts"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost btn-sm flex-1 gap-1"
          >
            <Mail size={14} /> Open Gmail Drafts
          </a>
          <button onClick={onClose} className="btn btn-primary btn-sm flex-1">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
